
// DataFilters.tsx
import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PropertyFilters } from '@/types/property';
import { Search } from 'lucide-react';
import type { DashboardStats } from '@/hooks/useUnifiedDashboardData';

interface DataFiltersProps {
  uniqueValues: {
    finalidade: string[];
    tipo: string[];
    bairro: string[];
    cidade: string[];
    origem: string[];
    variacao?: string[];
  };
  applyFilters: (filters: PropertyFilters) => void;
  stats: DashboardStats;
  filteredStats: DashboardStats;
  isMobile?: boolean;
  filters: PropertyFilters;
  setFilters: React.Dispatch<React.SetStateAction<PropertyFilters>>;
  bairroSearch: string;
  setBairroSearch: React.Dispatch<React.SetStateAction<string>>;
  tipoSearch: string;
  setTipoSearch: React.Dispatch<React.SetStateAction<string>>;
  clearFilters: () => void;
}

const DataFilters: React.FC<DataFiltersProps> = ({
  uniqueValues,
  applyFilters,
  stats,
  filteredStats,
  isMobile = false,
  filters,
  setFilters,
  bairroSearch,
  setBairroSearch,
  tipoSearch,
  setTipoSearch,
  clearFilters,
}) => {
  const updateCheckboxFilter = (key: keyof PropertyFilters, value: string, checked: boolean) => {
    setFilters((prev) => {
      const currentArray = (prev[key] as string[]) || [];
      if (checked) {
        return {
          ...prev,
          [key]: [...currentArray, value],
        };
      } else {
        return {
          ...prev,
          [key]: currentArray.filter((item) => item !== value),
        };
      }
    });
  };

  // Function to determine the appropriate max price based on filtered data
  const getRoundedMaxPrice = () => {
    const maxPrice = filteredStats.maxPrice || stats.maxPrice || 0;
    return Math.ceil(maxPrice / 10000) * 10000;
  };

  const handlePriceRangeChange = (value: [number, number]) => {
    const [min, max] = value;
    if (min > max) {
      setFilters((prev) => ({
        ...prev,
        valorMin: max,
        valorMax: max,
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        valorMin: min,
        valorMax: max,
      }));
    }
  };

  const handleBedroomRangeChange = (value: [number, number]) => {
    setFilters((prev) => ({
      ...prev,
      quartoMin: value[0],
      quartoMax: value[1],
    }));
  };

  const handleBathroomRangeChange = (value: [number, number]) => {
    setFilters((prev) => ({
      ...prev,
      banheiroMin: value[0],
      banheiroMax: value[1],
    }));
  };

  const handleParkingRangeChange = (value: [number, number]) => {
    setFilters((prev) => ({
      ...prev,
      vagaMin: value[0],
      vagaMax: value[1],
    }));
  };

  const handleAreaRangeChange = (value: [number, number]) => {
    setFilters((prev) => ({
      ...prev,
      areaUtilMin: value[0],
      areaUtilMax: value[1],
    }));
  };

  const handleAreaTotalRangeChange = (value: [number, number]) => {
    setFilters((prev) => ({
      ...prev,
      areaTotalMin: value[0],
      areaTotalMax: value[1],
    }));
  };

  const handleTempoAnunciadoRangeChange = (value: [number, number]) => {
    setFilters((prev) => ({
      ...prev,
      tempoAnunciadoMin: value[0],
      tempoAnunciadoMax: value[1],
    }));
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0,
    });
  };

  const filteredBairros = (uniqueValues.bairro || [])
    .filter((bairro) => bairro.toLowerCase().includes(bairroSearch.toLowerCase()))
    .sort((a, b) => a.localeCompare(b));

  const filteredTipos = (uniqueValues.tipo || [])
    .filter((tipo) => tipo.toLowerCase().includes(tipoSearch.toLowerCase()))
    .sort((a, b) => a.localeCompare(b));

  const variacaoValues = uniqueValues.variacao || [];

  // Calculate dynamic max values from filteredStats or fall back to stats
  const maxPrice = getRoundedMaxPrice();
  const minPrice = filteredStats.minPrice || stats.minPrice || 0;
  
  const maxBedrooms = Math.max(filteredStats.maxBedrooms || 0, 1);
  const minBedrooms = filteredStats.minBedrooms || 0;
  
  const maxBathrooms = Math.max(filteredStats.maxBathrooms || 0, 1);
  const minBathrooms = filteredStats.minBathrooms || 0;
  
  const maxParkingSpaces = Math.max(filteredStats.maxParkingSpaces || 0, 1);
  const minParkingSpaces = filteredStats.minParkingSpaces || 0;
  
  const maxUsefulArea = Math.ceil((filteredStats.maxUsefulArea || 0) / 50) * 50 || 2000;
  const minUsefulArea = filteredStats.minUsefulArea || 0;
  
  const maxTotalArea = Math.ceil((filteredStats.maxTotalArea || 0) / 100) * 100 || 20000;
  const minTotalArea = filteredStats.minTotalArea || 0;
  
  const maxDiasAnunciado = Math.max(filteredStats.maxDiasAnunciado || 0, 1);
  const minDiasAnunciado = filteredStats.minDiasAnunciado || 0;

  return (
    <div className="space-y-6">
      <Accordion
        type="multiple"
        defaultValue={isMobile ? ['finalidade', 'tipo'] : ['finalidade', 'tipo', 'preco', 'variacao']}
      >
        <AccordionItem value="finalidade">
          <AccordionTrigger>Finalidade</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2">
              {(uniqueValues.finalidade || []).map((finalidade) => (
                <div key={finalidade} className="flex items-center space-x-2">
                  <Checkbox
                    id={`finalidade-${finalidade}`}
                    checked={(filters.finalidade as string[])?.includes(finalidade)}
                    onCheckedChange={(checked) =>
                      updateCheckboxFilter('finalidade', finalidade, checked === true)
                    }
                  />
                  <Label htmlFor={`finalidade-${finalidade}`}>{finalidade}</Label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="tipo">
          <AccordionTrigger>Tipo de Imóvel</AccordionTrigger>
          <AccordionContent>
            <div className="mb-3 relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar tipo de imóvel..."
                value={tipoSearch}
                onChange={(e) => setTipoSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {filteredTipos.map((tipo) => (
                <div key={tipo} className="flex items-center space-x-2">
                  <Checkbox
                    id={`tipo-${tipo}`}
                    checked={(filters.tipo as string[])?.includes(tipo)}
                    onCheckedChange={(checked) =>
                      updateCheckboxFilter('tipo', tipo, checked === true)
                    }
                  />
                  <Label htmlFor={`tipo-${tipo}`}>{tipo}</Label>
                </div>
              ))}
              {filteredTipos.length === 0 && (
                <p className="text-sm text-muted-foreground py-2">Nenhum tipo encontrado</p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="cidade">
          <AccordionTrigger>Cidade</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {(uniqueValues.cidade || [])
                .sort((a, b) => a.localeCompare(b))
                .map((cidade) => (
                  <div key={cidade} className="flex items-center space-x-2">
                    <Checkbox
                      id={`cidade-${cidade}`}
                      checked={(filters.cidade as string[])?.includes(cidade)}
                      onCheckedChange={(checked) =>
                        updateCheckboxFilter('cidade', cidade, checked === true)
                      }
                    />
                    <Label htmlFor={`cidade-${cidade}`}>{cidade}</Label>
                  </div>
                ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="bairro">
          <AccordionTrigger>Bairro</AccordionTrigger>
          <AccordionContent>
            <div className="mb-3 relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar bairro..."
                value={bairroSearch}
                onChange={(e) => setBairroSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {filteredBairros.map((bairro) => (
                <div key={bairro} className="flex items-center space-x-2">
                  <Checkbox
                    id={`bairro-${bairro}`}
                    checked={(filters.bairro as string[])?.includes(bairro)}
                    onCheckedChange={(checked) =>
                      updateCheckboxFilter('bairro', bairro, checked === true)
                    }
                  />
                  <Label htmlFor={`bairro-${bairro}`}>{bairro}</Label>
                </div>
              ))}
              {filteredBairros.length === 0 && (
                <p className="text-sm text-muted-foreground py-2">Nenhum bairro encontrado</p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="preco">
          <AccordionTrigger>Faixa de Preço</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span>{formatCurrency(filters.valorMin || minPrice)}</span>
                <span>{formatCurrency(filters.valorMax || maxPrice)}</span>
              </div>
              <Slider
                value={[
                  Math.max(filters.valorMin || minPrice, minPrice),
                  Math.min(filters.valorMax || maxPrice, maxPrice)
                ]}
                min={minPrice}
                max={maxPrice}
                step={Math.max(1000, Math.floor(maxPrice / 100))}
                onValueChange={handlePriceRangeChange}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="variacao">
          <AccordionTrigger>Variação de Preço</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {variacaoValues.map((variacao) => (
                <div key={variacao} className="flex items-center space-x-2">
                  <Checkbox
                    id={`variacao-${variacao}`}
                    checked={(filters.variacao as string[])?.includes(variacao)}
                    onCheckedChange={(checked) =>
                      updateCheckboxFilter('variacao', variacao, checked === true)
                    }
                  />
                  <Label htmlFor={`variacao-${variacao}`}>
                    {variacao === 'Subiu'
                      ? 'Preço Subiu'
                      : variacao === 'Desceu'
                      ? 'Preço Desceu'
                      : variacao === 'Igual' || variacao === 'Manteve'
                      ? 'Preço Manteve'
                      : variacao}
                  </Label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="tempo-anunciado">
          <AccordionTrigger>Tempo Anunciado</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span>{filters.tempoAnunciadoMin || minDiasAnunciado} dias</span>
                <span>{filters.tempoAnunciadoMax || maxDiasAnunciado} dias</span>
              </div>
              <Slider
                value={[
                  Math.max(filters.tempoAnunciadoMin || minDiasAnunciado, minDiasAnunciado),
                  Math.min(filters.tempoAnunciadoMax || maxDiasAnunciado, maxDiasAnunciado)
                ]}
                min={minDiasAnunciado}
                max={maxDiasAnunciado}
                step={1}
                onValueChange={handleTempoAnunciadoRangeChange}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="quartos">
          <AccordionTrigger>Quartos</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span>{filters.quartoMin || minBedrooms}+</span>
                <span>até {filters.quartoMax || maxBedrooms}</span>
              </div>
              <Slider
                value={[
                  Math.max(filters.quartoMin || minBedrooms, minBedrooms),
                  Math.min(filters.quartoMax || maxBedrooms, maxBedrooms)
                ]}
                min={minBedrooms}
                max={maxBedrooms}
                step={1}
                onValueChange={handleBedroomRangeChange}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="banheiros">
          <AccordionTrigger>Banheiros</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span>{filters.banheiroMin || minBathrooms}+</span>
                <span>até {filters.banheiroMax || maxBathrooms}</span>
              </div>
              <Slider
                value={[
                  Math.max(filters.banheiroMin || minBathrooms, minBathrooms),
                  Math.min(filters.banheiroMax || maxBathrooms, maxBathrooms)
                ]}
                min={minBathrooms}
                max={maxBathrooms}
                step={1}
                onValueChange={handleBathroomRangeChange}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="vagas">
          <AccordionTrigger>Vagas de Garagem</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span>{filters.vagaMin || minParkingSpaces}+</span>
                <span>até {filters.vagaMax || maxParkingSpaces}</span>
              </div>
              <Slider
                value={[
                  Math.max(filters.vagaMin || minParkingSpaces, minParkingSpaces),
                  Math.min(filters.vagaMax || maxParkingSpaces, maxParkingSpaces)
                ]}
                min={minParkingSpaces}
                max={maxParkingSpaces}
                step={1}
                onValueChange={handleParkingRangeChange}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="area">
          <AccordionTrigger>Área Útil (m²)</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span>{filters.areaUtilMin || minUsefulArea}m²</span>
                <span>{filters.areaUtilMax || maxUsefulArea}m²</span>
              </div>
              <Slider
                value={[
                  Math.max(filters.areaUtilMin || minUsefulArea, minUsefulArea),
                  Math.min(filters.areaUtilMax || maxUsefulArea, maxUsefulArea)
                ]}
                min={minUsefulArea}
                max={maxUsefulArea}
                step={Math.max(5, Math.floor(maxUsefulArea / 40))}
                onValueChange={handleAreaRangeChange}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="area-total">
          <AccordionTrigger>Área Total (m²)</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span>{filters.areaTotalMin || minTotalArea}m²</span>
                <span>{filters.areaTotalMax || maxTotalArea}m²</span>
              </div>
              <Slider
                value={[
                  Math.max(filters.areaTotalMin || minTotalArea, minTotalArea),
                  Math.min(filters.areaTotalMax || maxTotalArea, maxTotalArea)
                ]}
                min={minTotalArea}
                max={maxTotalArea}
                step={Math.max(10, Math.floor(maxTotalArea / 50))}
                onValueChange={handleAreaTotalRangeChange}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="origem">
          <AccordionTrigger>Portal de Origem</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {(uniqueValues.origem || [])
                .sort((a, b) => a.localeCompare(b))
                .map((origem) => (
                  <div key={origem} className="flex items-center space-x-2">
                    <Checkbox
                      id={`origem-${origem}`}
                      checked={(filters.origem as string[])?.includes(origem)}
                      onCheckedChange={(checked) =>
                        updateCheckboxFilter('origem', origem, checked === true)
                      }
                    />
                    <Label htmlFor={`origem-${origem}`}>{origem}</Label>
                  </div>
                ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex flex-col space-y-2">
        <Button onClick={() => applyFilters(filters)}>Aplicar Filtros</Button>
        <Button variant="outline" onClick={clearFilters}>
          Limpar Filtros
        </Button>
      </div>
    </div>
  );
};

export default DataFilters;
